import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { merge, Observable, Subject } from 'rxjs';
import { WebSocketLabelDto } from '../dto/ws-label.dto';
import { environment } from '../../../../environments/environment';
import {
  PrinterSession,
  PrinterSessionService,
} from '../../../core/services/printers/printer-session.service';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class WebSocketApiLabelService {
  private socket: Socket | null = null;

  private basicWoLabelSubject = new Subject<
    WebSocketLabelDto['listToPrintBasicWorkOrderLabel']['result']
  >();
  basicWoLabel$ = this.basicWoLabelSubject.asObservable();

  private zplSubject = new Subject<
    WebSocketLabelDto['printZplLabel']['result']
  >();
  zpl$ = this.zplSubject.asObservable();

  private printed = new Map<string, number>();
  private TTL = 5 * 60 * 1000; // 5 minutes

  constructor(
    private sessionService: PrinterSessionService,
  ) {
    this.sessionService.session$.subscribe((session) => {
      if (session) {
        this.connect(session);
      } else {
        this.disconnect();
      }
    });
  }

  listToPrintBasicWorkOrderLabel(
    department?: string,
    printerName?: string
  ): Observable<WebSocketLabelDto['listToPrintBasicWorkOrderLabel']['result']> {
    if (!this.socket) {
      const extraHeaders: any = {};

      if (department) extraHeaders.department = department;
      if (printerName) extraHeaders.printerName = printerName;

      this.socket = io(environment.otherApis.apiBaseElite, {
        extraHeaders,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        timeout: 20000,
      });

      this.socket.on('connect', () => console.log('Socket connected'));
      this.socket.on('connect_error', (err) =>
        console.log('Socket error:', err)
      );
      this.socket.on('reconnect_attempt', () => console.log('Reconnecting...'));
    }

    const socketSubject = new Subject<
      WebSocketLabelDto['listToPrintBasicWorkOrderLabel']['result']
    >();

    this.socket.on('label/basic-wo/print', (data) => {
      socketSubject.next(data);
    });
    this.socket.on('label/zpl/print', (data) => {
      socketSubject.next(data);
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('Socket disconnected:', reason);
    });

    return socketSubject.asObservable();
  }

  listToPrintLabels(): Observable<
    WebSocketLabelDto['printLabel']['result']
  > {
    return merge(
      this.basicWoLabel$.pipe(
        map((data) => ({
          type: 'wo-basic' as const,
          data,
        }))
      ),
      this.zpl$.pipe(
        map((data) => ({
          type: 'zpl' as const,
          data,
        }))
      )
    );
  }

  closeConnection(): void {
    if (this.socket) {
      this.socket.emit('printer:disconnect');
      this.socket.disconnect();
    }
  }

  private disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private connect(session: PrinterSession): void {
    if (this.socket?.connected) {
      return;
    }

    const { printerName, department } = session;
    const extraHeaders: any = {};

    if (department) {
      extraHeaders.department = department;
    }
    if (printerName) {
      extraHeaders.printerName = printerName;
    }

    this.socket = io(environment.otherApis.apiBaseElite, {
      extraHeaders,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 20000,
      // transports: ['websocket'],
      // auth: session,
    });

    this.registerEvents();
  }

  private registerEvents(): void {
    this.socket.on('connect', () => console.log('Socket connected'));
    this.socket.on('connect_error', (err) => console.log('Socket error:', err));
    this.socket.on('reconnect_attempt', () => console.log('Reconnecting...'));

    this.socket.on('disconnect', (reason) => {
      console.warn('Socket disconnected:', reason);
    });

    // Basic WO Label
    this.socket.on('label/basic-wo/print', (data) => {
      this.basicWoLabelSubject.next(data);
    });

    // zpl labels
    this.socket.on('label/zpl/print', (data) => {
      console.log('zpl data', data);

      if (!this.markPrinted(data.id)) {
        return;
      }

      this.zplSubject.next(data);
    });
  }

  private markPrinted(id: string): boolean {
    const now = Date.now();

    if (this.printed.has(id)) {
      return false;
    }

    this.printed.set(id, now);

    for (const [key, time] of this.printed.entries()) {
      if (now - time > this.TTL) {
        this.printed.delete(key);
      }
    }
    return true;
  }
}
